import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Toggle, ImageUpload, RichTextEditor, Spinner } from '../../components/common';
import { newsService } from '../../services/newsService';
import { useForm } from '../../hooks';
import { validators } from '../../utils/validators';
import './NewsFormPage.css';

const NewsFormPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEditing = Boolean(slug);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const validationRules = {
    title: [validators.required, validators.minLength(5), validators.maxLength(200)],
    excerpt: [validators.required, validators.maxLength(300)],
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    validate
  } = useForm({
    title: '',
    excerpt: '',
    content: '',
    featuredImage: null,
    status: 'draft'
  }, validationRules);

  const fetchNews = useCallback(async () => {
    try {
      const response = await newsService.getBySlug(slug);
      const data = response.data;
      setValues({
        title: data.title || '',
        excerpt: data.excerpt || '',
        content: data.content || '',
        featuredImage: data.featuredImage || null,
        status: data.status || 'draft'
      });
    } catch (error) {
      toast.error('Failed to fetch news');
      navigate('/news');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate, setValues]);

  useEffect(() => {
    if (isEditing && slug) {
      fetchNews();
    }
  }, [isEditing, slug, fetchNews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the form errors');
      return;
    }
    
    setSaving(true);
    
    try {
      const formData = {
        ...values,
        featuredImage: values.featuredImage?.preview || values.featuredImage
      };
      
      if (isEditing) {
        await newsService.update(slug, formData);
        toast.success('News updated successfully');
      } else {
        await newsService.create(formData);
        toast.success('News created successfully');
      }
      
      navigate('/news');
    } catch (error) {
      toast.error(isEditing ? 'Failed to update news' : 'Failed to create news');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (e) => {
    setFieldValue('status', e.target.checked ? 'published' : 'draft');
  };

  if (loading) {
    return (
      <div className="form-loading">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="news-form-page">
      <div className="form-header">
        <Button 
          variant="ghost" 
          icon={<FiArrowLeft />}
          onClick={() => navigate('/news')}
        >
          Back to News
        </Button>
        <h2>{isEditing ? 'Edit News' : 'Add News'}</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-main">
            <Card>
              <div className="form-section">
                <div className={`sf-field ${errors.title && touched.title ? 'sf-field--error' : ''}`}>
                  <label className="sf-label" htmlFor="nf-title">
                    Title <span className="sf-required">*</span>
                  </label>
                  <input
                    id="nf-title"
                    name="title"
                    type="text"
                    className="sf-input"
                    placeholder="Enter news title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.title && touched.title && (
                    <span className="sf-error">{errors.title}</span>
                  )}
                </div>
              </div>

              <div className="form-section">
                <div className={`sf-field ${errors.excerpt && touched.excerpt ? 'sf-field--error' : ''}`}>
                  <label className="sf-label" htmlFor="nf-excerpt">
                    Excerpt <span className="sf-required">*</span>
                  </label>
                  <textarea
                    id="nf-excerpt"
                    name="excerpt"
                    className="sf-textarea"
                    placeholder="Enter a brief excerpt"
                    rows={3}
                    value={values.excerpt}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.excerpt && touched.excerpt && (
                    <span className="sf-error">{errors.excerpt}</span>
                  )}
                </div>
              </div>

              <div className="form-section">
                <RichTextEditor
                  label="Content"
                  value={values.content}
                  onChange={(content) => setFieldValue('content', content)}
                  placeholder="Write your news content here..."
                />
              </div>
            </Card>
          </div>

          <div className="form-sidebar">
            <Card title="Publish">
              <div className="publish-section">
                <Toggle
                  name="status"
                  checked={values.status === 'published'}
                  onChange={handleStatusChange}
                  label={values.status === 'published' ? 'Published' : 'Draft'}
                />
              </div>
              <div className="publish-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/news')}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  icon={<FiSave />}
                  loading={saving}
                  fullWidth
                >
                  {isEditing ? 'Update' : 'Save'}
                </Button>
              </div>
            </Card>

            <Card title="Featured Image">
              <ImageUpload
                value={values.featuredImage}
                onChange={(image) => setFieldValue('featuredImage', image)}
                label=""
              />
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NewsFormPage;
